import axiosInstance from '@/dataProvider/axios';
import type {
  RulesDocument,
  RulesDocuments,
  RulesScope,
  RulesVersion,
  SaveRulesBody,
} from './types';
import { RULES_VERSIONS_MAX_LIMIT } from './types';

const enc = encodeURIComponent;

export const RULES_ENDPOINT = (organizationId: string) => `/orgs/${enc(organizationId)}/rules`;
export const RULES_SCOPE_ENDPOINT = (organizationId: string, scope: RulesScope) =>
  `${RULES_ENDPOINT(organizationId)}/${scope}`;
export const RULES_VERSIONS_ENDPOINT = (organizationId: string, scope: RulesScope) =>
  `${RULES_SCOPE_ENDPOINT(organizationId, scope)}/versions`;

/**
 * Explicit org header: the rules routes are org-scoped and must not depend on ambient state.
 * `silent` stops the axios interceptor logging the response body (rules text) in local dev.
 */
function orgConfig(organizationId: string) {
  return { headers: { 'X-Organization-ID': organizationId }, silent: true as const };
}

function normalizeDocument(raw: Partial<RulesDocument> | null | undefined): RulesDocument {
  return {
    version: typeof raw?.version === 'number' ? raw.version : 0,
    content: typeof raw?.content === 'string' ? raw.content : null,
    template: typeof raw?.template === 'string' ? raw.template : null,
    updated_at: raw?.updated_at ?? null,
    updated_by_user_id: raw?.updated_by_user_id ?? null,
  };
}

export const rulesApi = {
  async get(organizationId: string): Promise<RulesDocuments> {
    const { data } = await axiosInstance.get<Partial<RulesDocuments>>(RULES_ENDPOINT(organizationId), orgConfig(organizationId));
    return {
      org: normalizeDocument(data?.org),
      mine: normalizeDocument(data?.mine),
      max_bytes: typeof data?.max_bytes === 'number' ? data.max_bytes : null,
    };
  },

  async save(
    organizationId: string,
    scope: RulesScope,
    content: string,
    baseVersion: number,
  ): Promise<RulesDocument> {
    const body: SaveRulesBody = { content, base_version: baseVersion };
    const { data } = await axiosInstance.put<Partial<RulesDocument>>(
      RULES_SCOPE_ENDPOINT(organizationId, scope),
      body,
      orgConfig(organizationId),
    );
    return normalizeDocument(data);
  },

  async versions(organizationId: string, scope: RulesScope, limit = 20): Promise<RulesVersion[]> {
    const clamped = Math.max(1, Math.min(RULES_VERSIONS_MAX_LIMIT, Math.floor(limit)));
    const { data } = await axiosInstance.get<{ versions?: RulesVersion[] }>(
      RULES_VERSIONS_ENDPOINT(organizationId, scope),
      { params: { limit: clamped }, ...orgConfig(organizationId) },
    );
    return data?.versions ?? [];
  },
};
